import type { ListBuilderConfiguration } from '../types';

/**
 * Issue list configuration
 * Main issues list with full features including filtering, sorting, and actions
 * Converted from JSON to TypeScript for type safety
 */
export const ISSUE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-issues-all',
			cacheTTL: 600000,
			name: 'All Issues',
			type: 'default',
			active: true,
			sortOrder: 0,
			filterRules: {
				issueStatus: {
					defaultValue: [
						{ id: 'To-Do', label: 'To-Do' },
						{ id: 'In Progress', label: 'In Progress' },
					],
				},
				issueAssignee: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: false },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: true },
						],
					},
				},
				issueClientSalesRep: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: true },
						],
					},
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: true },
						],
					},
				},
			},
			columns: [
				{
					id: 'issue-name-type',
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'issue',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'name', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'md',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'type-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'type', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					label: 'Name',
					headers: [{ id: 'h2', text: 'Name' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					sortFields: [
						{ direction: 'ASC', fieldName: 'name', objectTable: 'issue' },
					],
					width: 'w-32',
				},
				{
					id: 'issue-status',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'status', objectTable: 'issue' },
							},
							displayStyle: 'custom',
							customComponent: { id: 'issue-status-selector' },
						},
					],
					label: 'Status',
					headers: [{ id: 'h1', text: 'Status' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					sortFields: [
						{ direction: 'ASC', fieldName: 'status', objectTable: 'issue' },
					],
					width: 'w-16',
				},
				{
					id: 'issue-priority',
					fields: [
						{
							id: 'priority-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'priority', objectTable: 'issue' },
							},
							displayStyle: 'custom',
							customComponent: { id: 'issue-priority-selector' },
						},
					],
					label: 'Priority',
					headers: [{ id: 'h-priority', text: 'Priority' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					sortFields: [
						{ direction: 'DESC', fieldName: 'priority', objectTable: 'issue' },
					],
					width: 'w-24',
				},
				{
					id: 'issue-owner',
					fields: [
						{
							id: 'owner-name-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'assigneeUserName', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					label: 'Assigned To',
					headers: [{ id: 'h-owner', text: 'Assigned To' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'ownerUserName',
							objectTable: 'issue',
						},
					],
					width: 'w-28',
				},
				{
					id: 'issue-description',
					fields: [
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'description', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								textExpansion: { enabled: true, maxLines: 3 },
							},
							displayStyle: 'text',
						},
					],
					label: 'Description',
					headers: [{ id: 'h3', text: 'Description' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					width: 'flex-1',
				},
				{
					id: 'issue-created-date',
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'createdDate', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'date',
						},
					],
					label: 'Created',
					headers: [{ id: 'h-created', text: 'Created' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: '_created_date',
							objectTable: 'issue',
						},
					],
					width: 'w-28',
				},
				{
					id: 'related-entities',
					maxDisplay: 2,
					fields: [
						{
							id: 'related-clients',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'clients', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'related_entity_list',
							displayConfig: {
								entityType: 'client',
								icon: 'users',
								linkPattern: '/clients/{id}',
								nameField: 'name',
								separator: 'line_break',
							},
						},
						{
							id: 'related-vendors',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'vendors', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'related_entity_list',
							displayConfig: {
								entityType: 'vendor',
								icon: 'truck',
								linkPattern: '/vendors/{id}',
								nameField: 'name',
								separator: 'line_break',
							},
						},
					],
					label: 'Related To',
					headers: [{ id: 'h4', text: 'Related To' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					width: 'w-48',
				},
				{
					id: 'issue-actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					label: 'Actions',
					headers: [{ id: 'h-actions', text: '' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					width: 'w-24',
				},
			],
			defaultSortId: 'column-issue-priority-desc',
			clickAction: {
				type: 'navigate',
				path: '/issues/{id}',
			},
			actions: [
				{
					id: 'create-issue',
					label: 'Create Issue',
					icon: 'plus',
					type: 'create',
					position: 'header',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'issueKeywords',
			'issueStatus',
			'issueType',
			'issuePriority',
			'issueClientSalesRep',
			'issueClient',
			'issueAssignee',
			'issueCreatedDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/issues/:id',
			objectLabel: 'Issue',
			objectTable: 'issue',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'priority', fieldLabel: 'Priority' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
				{ fieldName: '_modified_date', fieldLabel: 'Modified Date' },
				{ fieldName: 'assigneeUserName', fieldLabel: 'Assigned To' },
			],
		},
	],
};
